import { useCallback, useEffect, useState } from "react";
import {
  ResolvePageService,
  type PageResponse,
  type ResolveCallbackRow,
  type ResolveCallbacksSummary,
  type ResolveDisputeEventRow,
  type ResolveFocusSummary,
  type ResolveFrictionAdvisoryRow,
  type ResolveFrictionNegativeOverrideRow,
  type ResolveFrictionRepeatRow,
  type ResolveFrictionSummary,
  type ResolveHoldbackRow,
  type ResolveHoldbacksSummary,
  type ResolveIssueQuickView,
  type ResolveIssueRow,
  type ResolveIssuesSummary,
  type ResolveRange,
} from "@/services/resolve-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

function usePage<T>(enabled: boolean | undefined, deps: unknown[], load: () => Promise<PageResponse<T>>) {
  const [state, setState] = useState<PageResponse<T>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void load().then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { ...state, loading };
}

function useSummary<T>(enabled: boolean | undefined, deps: unknown[], load: () => Promise<{ data: T; error: unknown | null }>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void load().then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, error, loading };
}

export function useResolveCallbacksData(query: { enabled?: boolean; page: number; pageSize: number; range: ResolveRange; search?: string }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = usePage<ResolveCallbackRow>(query.enabled, [query.page, query.pageSize, query.range, query.search, refreshToken], () =>
    ResolvePageService.getCallbacksPage({ ...query, refreshToken }),
  );
  const summary = useSummary<ResolveCallbacksSummary>(query.enabled, [refreshToken], () => ResolvePageService.getCallbacksSummary(refreshToken));
  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  return { ...page, summary: summary.data, summaryLoading: summary.loading, summaryError: summary.error, refresh };
}

export function useResolveHoldbacksData(query: { enabled?: boolean; page: number; pageSize: number; bucket?: "all" | "active" | "paused" | "resolved"; search?: string }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const holdbacks = usePage<ResolveHoldbackRow>(query.enabled, [query.page, query.pageSize, query.bucket, query.search, refreshToken], () =>
    ResolvePageService.getHoldbacksPage({ ...query, refreshToken }),
  );
  const summary = useSummary<ResolveHoldbacksSummary>(query.enabled, [refreshToken], () => ResolvePageService.getHoldbacksSummary(refreshToken));
  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  return { ...holdbacks, summary: summary.data, summaryLoading: summary.loading, summaryError: summary.error, refresh, refreshToken };
}

export function useResolveDisputeEventsPage(query: { enabled?: boolean; page: number; pageSize: number; search?: string; refreshToken?: number }) {
  return usePage<ResolveDisputeEventRow>(query.enabled, [query.page, query.pageSize, query.search, query.refreshToken], () =>
    ResolvePageService.getDisputeEventsPage(query),
  );
}

export function useResolveIssuesData(query: {
  enabled?: boolean;
  page: number;
  pageSize: number;
  quickView?: ResolveIssueQuickView;
  search?: string;
  issueType?: string;
  severity?: string;
  branch?: string;
  region?: string;
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = usePage<ResolveIssueRow>(query.enabled, [query.page, query.pageSize, query.quickView, query.search, query.issueType, query.severity, query.branch, query.region, refreshToken], () =>
    ResolvePageService.getIssuesPage({ ...query, refreshToken }),
  );
  const summary = useSummary<ResolveIssuesSummary>(query.enabled, [refreshToken], () => ResolvePageService.getIssuesSummary(refreshToken));
  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  return { ...page, summary: summary.data, summaryLoading: summary.loading, summaryError: summary.error, refresh };
}

export function useResolveFrictionData(query: { enabled?: boolean; range: ResolveRange; pageSize?: number }) {
  const [refreshToken, setRefreshToken] = useState(0);
  const advisoryPage = usePage<ResolveFrictionAdvisoryRow>(query.enabled, [query.range, query.pageSize, refreshToken], () =>
    ResolvePageService.getFrictionAdvisoriesPage({ page: 0, pageSize: query.pageSize ?? 50, range: query.range, refreshToken }),
  );
  const overridePage = usePage<ResolveFrictionNegativeOverrideRow>(query.enabled, [query.range, query.pageSize, refreshToken], () =>
    ResolvePageService.getFrictionNegativeOverridesPage({ page: 0, pageSize: query.pageSize ?? 50, range: query.range, refreshToken }),
  );
  const repeatDisputes = usePage<ResolveFrictionRepeatRow>(query.enabled, [query.pageSize, refreshToken], () =>
    ResolvePageService.getFrictionRepeatDisputesPage({ page: 0, pageSize: query.pageSize ?? 25, refreshToken }),
  );
  const repeatReversals = usePage<ResolveFrictionRepeatRow>(query.enabled, [query.pageSize, refreshToken], () =>
    ResolvePageService.getFrictionRepeatReversalsPage({ page: 0, pageSize: query.pageSize ?? 25, refreshToken }),
  );
  const summary = useSummary<ResolveFrictionSummary>(query.enabled, [refreshToken], () => ResolvePageService.getFrictionSummary(refreshToken));
  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  return { advisoryPage, overridePage, repeatDisputes, repeatReversals, summary: summary.data, summaryLoading: summary.loading, summaryError: summary.error, refresh };
}

export function useResolveFocusSummary(query: { enabled?: boolean; refreshToken?: number } = {}) {
  return useSummary<ResolveFocusSummary>(query.enabled, [query.refreshToken], () => ResolvePageService.getFocusSummary(query.refreshToken));
}
